// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tooltip from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xCommonUpdate } from "@vanmeter/App/Handlers/XNG/Common";
import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import DataTable, { DataTableProps } from "@vanmeter/MobiusOverrides/DataTable/DataTable";
import { fontConfig } from "@vanmeter/XDL/constants";
import Check from "@vanmeter/XDL/Icons/IconCheck";
import { color, getSystemColor, LoadingDots } from "@vanmeter/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-Changes: VM - Classic
    additionalResults?: { [key: string]: string } | null;
    totalWarehouses?: number;
    leadTime?: string;
    view?: string;
    locationLinkLabel?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const isPDP = getCurrentPage(state).type === "ProductDetailsPage";
    const currentLocation = getLocation(state);
    const returnUrl = encodeURIComponent(`${currentLocation.pathname}${currentLocation.search}`);
    const signInPageUrl = (getPageLinkByPageType(state, "SignInPage")?.url || "") + `?returnUrl=${returnUrl}`;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        showInventory: state.context.showInventory,
        // XNG-Changes: VM - Classic
        signInPageUrl,
        // XNG-Changes
        isAuthenticated: xIsAuthenticated(state),
        isGuest: getCurrentUserIsGuest(state),
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    xCommonUpdate,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Changes
    inventoryWrapper?: InjectableCss;
    availLinkWrapper?: InjectableCss;
    modalStatusWrapper?: InjectableCss;
    modalStatuslabelText?: TypographyPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
};

// XNG-Changes
const InventoryMessage = styled.div<any>`
    display: flex;
    align-items: center;
    border: 1px solid ${props => getSystemColor(props.color)};
    border-color: ${props => getSystemColor(props.color)};

    .label-wrapper {
        display: flex;
        flex: 1;
        align-items: center;
        padding: 4px 6px;
        > div {
            line-height: 1;
        }
    }

    .badge-wrapper {
        display: flex;
        align-items: center;
        background-color: ${props => getSystemColor(props.color)};
        outline: 1px solid ${props => getSystemColor(props.color)};
        padding: 4px 6px;
        @media print {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }
        ${IconWrapper},svg {
            width: 11px;
            height: 16px;
        }
        .label-text {
            color: ${color("common.white")};
        }
    }
    .label-text {
        font-size: ${fontConfig.xSmallSingleLine.size}px;
        line-height: ${fontConfig.xSmallSingleLine.lineHeight};
        font-weight: ${fontConfig.weight.bold};
    }
    ${injectCss}
`;

// const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
//     if (!productSettings.showInventoryAvailability) {
//         return "common";
//     }

//     const availabilityType = availability?.messageType;
//     if (availabilityType === AvailabilityMessageType.OutOfStock) {
//         return "danger";
//     }

//     if (availabilityType === AvailabilityMessageType.LowStock) {
//         return "warning";
//     }

//     return "success";
// };

// const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
//     if (!productSettings.showInventoryAvailability) {
//         return undefined;
//     }

//     const availabilityType = availability?.messageType;
//     if (availabilityType === AvailabilityMessageType.OutOfStock) {
//         return X;
//     }

//     if (availabilityType === AvailabilityMessageType.LowStock) {
//         return AlertTriangle;
//     }

//     return Check;
// };

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

// <XNG-Changes>
const InventoryBlock = ({
    color,
    badge,
    label,
    tooltip,
    styles,
}: {
    color?: string;
    badge?: string | React.ReactNode;
    label?: string | React.JSX.Element | React.JSX.Element[];
    tooltip?: string;
    styles?: InjectableCss;
}) => (
    <InventoryMessage color={color} {...styles}>
        {badge && <StyledWrapper className="badge-wrapper">{badge}</StyledWrapper>}
        <StyledWrapper className="label-wrapper">
            <Typography className="label-text">{label}</Typography>
            {tooltip && <Tooltip text={tooltip} />}
        </StyledWrapper>
    </InventoryMessage>
);

const AvailabilityBadge = ({
    isAuthenticated,
    totalWarehouses,
}: {
    isAuthenticated: boolean;
    totalWarehouses?: string | number;
}) => {
    const shouldShowCount = isAuthenticated && !!totalWarehouses;

    return shouldShowCount ? (
        <Typography className="label-text">{totalWarehouses}</Typography>
    ) : (
        <Icon src={Check} color="common.white" />
    );
};
// </XNG-Changes>

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-Changes: VM - Classic
    additionalResults,
    totalWarehouses,
    leadTime,
    signInPageUrl,
    isAuthenticated,
    isGuest,
    view,
    locationLinkLabel,
    xCommonUpdate,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    // XNG-Changes
    const [totalQtyOnHandWarehouses, setTotalQtyOnHandWarehouses] = useState<number | undefined>(undefined);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
        // XNG-Changes
        xCommonUpdate({ key: "isAvailabilityByWarehouseModalOpen", value: false });
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        // XNG-Changes
        xCommonUpdate({ key: "isAvailabilityByWarehouseModalOpen", value: true });
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                // XNG-Changes: add sort to match with classic behavior
                setWarehouses(
                    result.warehouses
                        ?.slice()
                        .sort((a, b) => (a.description || a.name).localeCompare(b.description || b.name)),
                );
                // XNG-Changes: use qtyOnHand from inventory API
                setTotalQtyOnHandWarehouses((result as typeof result & { qtyOnHand?: number }).qtyOnHand);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    // const color = getAvailabilityColor(productSettings, availability);
    // const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    // XNG-Changes: VM - Classic

    const availabilityComponentVM = () => {
        const messageType = availability?.messageType;
        // XNG-Changes: show loading dots while availability is being fetched
        if (!availability) {
            return (
                <StyledWrapper {...styles.inventoryWrapper}>
                    <LoadingDots />
                </StyledWrapper>
            );
        }

        const factoryInventory = additionalResults?.factoryDirectInventory;
        const hasFactoryInventory = !!factoryInventory?.trim();
        const isAvailable =
            messageType === AvailabilityMessageType.Available || messageType === AvailabilityMessageType.LowStock;
        const isOutOfStock = messageType === AvailabilityMessageType.OutOfStock;

        return (
            <StyledWrapper {...styles.inventoryWrapper}>
                {isAvailable && (
                    <InventoryBlock
                        color="success"
                        badge={<AvailabilityBadge isAuthenticated={isAuthenticated} />}
                        label={`${availability?.message} - ${translate("Van Meter Inc.")}`}
                        tooltip={siteMessage("Van_In_Stock") as string}
                    />
                )}
                {isOutOfStock && (
                    <InventoryBlock
                        color="common.ui4"
                        label={
                            hasFactoryInventory
                                ? `${availability?.message} - ${translate("Van Meter Inc.")}`
                                : availability?.message
                        }
                    />
                )}
                {hasFactoryInventory && (
                    <InventoryBlock
                        color="success"
                        badge={<AvailabilityBadge isAuthenticated={isAuthenticated} />}
                        label={translate("In Stock - Factory Direct")}
                        tooltip={siteMessage("Factory_In_Stock") as string}
                    />
                )}
            </StyledWrapper>
        );
    };

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    // XNG-Changes
    // let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    // if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
    //     inventoryTextComponent = (
    //         <InventoryMessage color={color} {...styles.inventoryMessage}>
    //             <Icon color={color} src={iconSrc} size={12} />
    //             <Typography {...styles.messageText} data-test-selector="availabilityMessage">
    //                 {availability.message}
    //             </Typography>
    //         </InventoryMessage>
    //     );
    // } else if (availability) {
    //     inventoryTextComponent = <></>;
    // }

    return (
        <StyledWrapper {...styles.container} {...{ view }}>
            <StyledWrapper {...styles.messageGridItem}>{availabilityComponentVM()}</StyledWrapper>
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <StyledWrapper {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                        aria-label="Location Availability"
                    >
                        {/* XNG-Changes */}

                        {locationLinkLabel ||
                            (isProductDetailsPage ? translate("Location Availability") : translate("Location Avail."))}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        // XNG-Changes
                        headline={translate("Availability")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <>
                                        {/* XNG-Changes */}
                                        <StyledWrapper {...styles.modalStatusWrapper}>
                                            <Typography {...styles.modalStatuslabelText}>
                                                {translate("Total Availability")}:
                                            </Typography>
                                            {totalQtyOnHandWarehouses && totalQtyOnHandWarehouses > 0 ? (
                                                <Typography
                                                    {...styles.modalStatuslabelText}
                                                    className="warehouse-count"
                                                >
                                                    {isAuthenticated || isGuest
                                                        ? totalQtyOnHandWarehouses
                                                        : translate("Yes")}
                                                </Typography>
                                            ) : (
                                                <InventoryBlock
                                                    color="common.ui4"
                                                    label={translate("Out of Stock")}
                                                    tooltip={translate(
                                                        `Lead time for this product is typically ${leadTime || 0} days`,
                                                    )}
                                                />
                                            )}
                                        </StyledWrapper>
                                        <DataTable
                                            {...styles.availabilityByWarehouseTable}
                                            data-test-selector="availabilityByWarehouseModal"
                                        >
                                            <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                    {/* XNG-Changes */}
                                                    {translate("Address")}
                                                </DataTableHeader>
                                                <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                    {/* XNG-Changes */}
                                                    {translate("Availability")}
                                                </DataTableHeader>
                                            </DataTableHead>
                                            <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                                {warehouses?.map(
                                                    (
                                                        // XNG-Changes: Added properties to warehouse for inventory display purposes
                                                        warehouse: WarehouseDto & {
                                                            properties?: { [key: string]: string };
                                                        },
                                                    ) => (
                                                        <DataTableRow
                                                            {...styles.availabilityByWarehouseTableRow}
                                                            key={warehouse.name}
                                                        >
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Description"
                                                                // XNG-Changes
                                                                {...{ header: translate("Address") }}
                                                            >
                                                                {warehouse.description || warehouse.name}
                                                            </DataTableCell>
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Quantity"
                                                                // XNG-Changes
                                                                {...{ header: translate("Availability") }}
                                                            >
                                                                {/* XNG-Changes: VM - Classic */}
                                                                {isAuthenticated
                                                                    ? warehouse?.qty
                                                                    : (warehouse?.qty ?? 0) > 0
                                                                      ? translate("Yes")
                                                                      : 0}
                                                                &nbsp;
                                                                {translate("Available at this Location")}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    ),
                                                )}
                                            </DataTableBody>
                                        </DataTable>
                                    </>
                                )}
                            </>
                        )}
                    </Modal>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
